import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import { getPrimaryColors } from 'src/components/utils/theme/getPrimaryColors';
import { ThemeType } from 'src/components/utils/theme/ThemeType';
import { zIndex } from 'src/utils/widget/zIndex';

declare module '@mui/material/styles' {
    interface Palette {
        button: { main: string };
    }
    interface PaletteOptions {
        button?: { main?: string };
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        button: true;
    }
}

// Chrome/Safari autofill states. The browser re-asserts its own autofill paint per
// state, so all four must be listed for the override below to stick.
const webkitAutofillSelector = [
    '& input:-webkit-autofill',
    '& input:-webkit-autofill:hover',
    '& input:-webkit-autofill:focus',
    '& input:-webkit-autofill:active',
].join(', ');

export const MUITheme = (customizations: ThemeType) => {
    const isDark = customizations.darkMode === true;
    // Semantic surface / on-surface tokens. In dark mode these redefine
    // palette.common.white/black so every component using common.* gets the
    // right color without component-level edits. In light mode common.* stays
    // at MUI's canonical '#fff'/'#000' — zero regression for existing tenants.
    const surfaceColor = customizations.widgetBackgroundColor ?? '#ffffff';
    const onSurfaceColor = customizations.widgetTextColor ?? customizations.bodyColor;

    // Grey scale derives from `lightestGrey`. Light mode darkens progressively
    // for visible borders/hover tints against a near-white surface; dark mode
    // lightens so the same higher-index = higher-contrast semantic holds
    // against a dark surface. Light-mode output is byte-identical to the
    // pre-dark-mode darken() chain.
    const greyScale = (base: string, dark: boolean) => {
        const fn = dark ? lighten : darken;
        return {
            '50': base,
            '100': fn(base, 0.05),
            '200': fn(base, 0.1),
            '300': fn(base, 0.15),
            '400': fn(base, 0.2),
            '500': fn(base, 0.3),
            '600': fn(base, 0.4),
            '700': fn(base, 0.5),
            '800': fn(base, 0.6),
            '900': fn(base, 0.7),
        };
    };

    // Dark-mode standard-Alert factory. MUI's default standard-variant
    // alpha(<severity>.light, 0.16) reads as a near-invisible tint on a dark
    // surface; we want a more solid background tint plus explicit body/icon
    // colors. `informationColor` typically lives on the desaturated end so
    // it gets a higher background alpha and a body-color icon (the
    // information-color icon would otherwise vanish on the panel).
    const darkAlertVariant = (
        severityColor: string,
        opts: { bgAlpha?: number; iconColor?: string } = {},
    ) => ({
        backgroundColor: alpha(severityColor, opts.bgAlpha ?? 0.18),
        color: customizations.bodyColor,
        '& .MuiAlert-icon': { color: opts.iconColor ?? severityColor },
    });

    const theme = createTheme({
        spacing: customizations.baseUnit,
        shape: {
            borderRadius: parseInt(customizations.borderRadius.toString()),
        },
        palette: {
            mode: isDark ? 'dark' : 'light',
            // In dark mode, redefine common.white/black as semantic surface/on-surface
            // tokens so every component using theme.palette.common.* gets the right color.
            // In light mode we omit the key entirely so MUI's canonical '#fff'/'#000'
            // defaults stay intact — guarantees zero regression for light-mode tenants.
            ...(isDark && {
                common: {
                    white: surfaceColor,
                    black: onSurfaceColor,
                },
            }),
            primary: {
                main: customizations.primaryColor,
                contrastText: customizations.primaryColorContrast,
            },
            secondary: {
                main: customizations.secondaryColor,
                contrastText: customizations.secondaryColorContrast,
            },
            error: {
                main: customizations.errorColor,
            },
            warning: {
                main: customizations.warningColor,
            },
            text: {
                // Light mode: unchanged fallback chain — zero regression for
                // existing tenants. Dark mode: use a readable on-surface
                // secondary (0.87 opacity) so "color=secondary" text on
                // checkout summaries stays legible, and don't let the tenant's
                // secondaryColor (often a dark brand accent) bleed into text.
                primary: isDark
                    ? (customizations.widgetTextColor ?? '#fff')
                    : (customizations.widgetTextColor ?? customizations.primaryColor),
                // Use pure white as the dark-mode secondary base (not the
                // tenant's widgetTextColor which may be a warm-off-white like
                // '#e0e0e0') so secondary labels read brighter against dark
                // surfaces. 0.9 opacity keeps a subtle hierarchy below primary.
                // An explicit `widgetTextSecondaryColor` wins in either mode — the
                // opt-out for tenants who don't want the derived 60% dimming (e.g.
                // a near-black tenant that wants fully-black secondary text).
                secondary:
                    customizations.widgetTextSecondaryColor ??
                    (isDark
                        ? alpha('#fff', 0.9)
                        : customizations.widgetTextColor
                          ? alpha(customizations.widgetTextColor, 0.6)
                          : customizations.secondaryColor),
            },
            grey: greyScale(customizations.lightestGrey, isDark),
            button: {
                main: customizations.primaryColor,
            },
            background: {
                paper: surfaceColor,
                default: surfaceColor,
            },
        },
        typography: {
            fontFamily: customizations.fontFamily,
            fontSize: customizations.fontSize,
            body1: {
                fontSize: customizations.bodySize,
                fontFamily: customizations.bodyFont,
                fontWeight: customizations.bodyFontWeight,
                color: customizations.bodyColor,
            },
            body2: {
                fontSize: customizations.bodySize,
                fontFamily: customizations.bodyFont,
                fontWeight: customizations.bodyFontWeight,
                color: customizations.bodyColor,
            },
            h1: {
                fontSize: customizations.h1Size,
                fontFamily: customizations.h1Font,
                fontWeight: customizations.h1FontWeight,
                color: customizations.h1Color,
            },
            h2: {
                fontSize: customizations.h2Size,
                fontFamily: customizations.h2Font,
                fontWeight: customizations.h2FontWeight,
                color: customizations.h2Color,
            },
            h3: {
                fontSize: customizations.h3Size,
                fontFamily: customizations.h3Font,
                fontWeight: customizations.h3FontWeight,
                color: customizations.h3Color,
            },
            h4: {
                fontSize: customizations.h4Size,
                fontFamily: customizations.h4Font,
                fontWeight: customizations.h4FontWeight,
                color: customizations.h4Color,
            },
            h5: {
                fontSize: customizations.h5Size,
                fontFamily: customizations.h5Font,
                fontWeight: customizations.h5FontWeight,
                color: customizations.h5Color,
            },
            h6: {
                fontSize: customizations.h6Size,
                fontFamily: customizations.h6Font,
                fontWeight: customizations.h6FontWeight,
                color: customizations.h6Color,
            },
        },
        breakpoints: {
            values: customizations.breakpoints,
        },
        components: {
            MuiFormControl: {
                styleOverrides: {
                    marginDense: {
                        marginTop: 0,
                        marginBottom: '4px',
                    },
                },
            },
            MuiUseMediaQuery: {
                defaultProps: {
                    noSsr: true,
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: `rgba(0, 0, 0, 0.8)`,
                    },
                    invisible: {
                        opacity: '0 !important',
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: customizations.primaryColor,
                    },
                },
            },
            MuiLink: {
                defaultProps: {
                    underline: customizations.linkStyle === 'underline' ? 'always' : 'hover',
                },
                styleOverrides: {
                    root: {
                        color: customizations.linkColor,
                        '&:hover': {
                            color: customizations.primaryColor,
                        },
                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColor}`,
                        },
                    },
                },
            },
            MuiScopedCssBaseline: {
                styleOverrides: {
                    root: {
                        '*': {
                            boxSizing: 'border-box',
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        zIndex: zIndex.alwaysVisible,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontWeight: customizations.buttonFontWeight ?? 600,
                        fontSize: customizations.buttonFontSize ?? customizations.fontSize,
                        fontFamily: customizations.buttonFont,
                        textTransform: 'none',

                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColor}, \
                                        0 0 0 3px ${customizations.primaryColorContrast}`,
                        },
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover, &:active': {
                            boxShadow: 'none',
                        },
                        paddingLeft: `${customizations.containedButtonLeftRightPadding}px !important`,
                        paddingRight: `${customizations.containedButtonLeftRightPadding}px !important`,
                    },
                    containedPrimary: {
                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColorContrast}, \
                                        0 0 0 3px ${customizations.primaryColor}`,
                        },
                    },
                    outlined: {
                        borderWidth: `${customizations.outlinedButtonBorderWidth}px !important`,
                        paddingLeft: `${customizations.outlinedButtonLeftRightPadding}px !important`,
                        paddingRight: `${customizations.outlinedButtonLeftRightPadding}px !important`,
                    },
                    outlinedPrimary: {
                        borderColor: customizations.outlinedButtonBorderColor,
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        padding: '12px',
                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColor}, \
                                        0 0 0 3px ${customizations.primaryColorContrast}`,
                        },
                    },
                    colorPrimary: {
                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColorContrast}, \
                                        0 0 0 3px ${customizations.primaryColor}`,
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    action: {
                        '& .MuiIconButton-root': {
                            padding: '5px',
                        },
                    },
                    // In dark mode, MUI's default standard-variant Alert uses
                    // alpha(<severity>.light, 0.16) for the background. With
                    // tenant-supplied severity colors that auto-derive into
                    // pale tones, that produces a near-invisible tint on a
                    // dark surface (the warning box just fades into the page).
                    // Give each severity a more solid tint plus an explicit
                    // text/icon color so the alert reads clearly. Light mode
                    // is left at MUI defaults — zero regression.
                    ...(isDark && {
                        standardWarning: darkAlertVariant(customizations.warningColor),
                        standardError: darkAlertVariant(customizations.errorColor),
                        standardInfo: darkAlertVariant(customizations.informationColor, {
                            bgAlpha: 0.4,
                            iconColor: customizations.bodyColor,
                        }),
                        standardSuccess: darkAlertVariant(customizations.primaryColor),
                    }),
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    grouped: {
                        '&:not(:last-of-type)': {
                            borderTopRightRadius: customizations.borderRadius,
                            borderBottomRightRadius: customizations.borderRadius,
                            borderTopLeftRadius: customizations.borderRadius,
                            borderBottomLeftRadius: customizations.borderRadius,
                            borderWidth: customizations.outlinedButtonBorderWidth,
                            // See MuiToggleButton.root above for the rationale —
                            // soften the border to bodyColor at 0.4 alpha in dark
                            // mode so the unselected pill outlines stay visible
                            // against the dark page surface.
                            borderColor: isDark
                                ? alpha(customizations.bodyColor, 0.4)
                                : customizations.secondaryColor,
                        },
                        '&:last-of-type': {
                            borderTopRightRadius: customizations.borderRadius,
                            borderBottomRightRadius: customizations.borderRadius,
                            borderTopLeftRadius: customizations.borderRadius,
                            borderBottomLeftRadius: customizations.borderRadius,
                            borderWidth: customizations.outlinedButtonBorderWidth,
                            // See MuiToggleButton.root above for the rationale —
                            // soften the border to bodyColor at 0.4 alpha in dark
                            // mode so the unselected pill outlines stay visible
                            // against the dark page surface.
                            borderColor: isDark
                                ? alpha(customizations.bodyColor, 0.4)
                                : customizations.secondaryColor,
                        },
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        fontWeight: customizations.buttonFontWeight ?? 600,
                        fontSize: customizations.buttonFontSize ?? customizations.fontSize,
                        fontFamily: customizations.buttonFont,
                        // Light mode: keep secondaryColor for both text and border —
                        // tenants tune that to a readable dark brand tone on white.
                        // Dark mode: secondaryColor is typically a dark brand tone
                        // that disappears on a dark surface, so use the body text
                        // color for the label and a softened version for the border
                        // (full-strength border is too loud for an unselected state).
                        borderColor: isDark
                            ? alpha(customizations.bodyColor, 0.4)
                            : customizations.secondaryColor,
                        color: isDark ? customizations.bodyColor : customizations.secondaryColor,
                        margin: customizations.baseUnit * 2,
                        padding: `${customizations.baseUnit * 1}px ${
                            customizations.baseUnit * 1.5
                        }px`,
                        transition: '0.15s ease-in-out',

                        '&:hover': {
                            backgroundColor: isDark
                                ? lighten(customizations.lightestGrey, 0.1)
                                : darken(customizations.lightestGrey, 0.05),
                            color: isDark
                                ? customizations.bodyColor
                                : customizations.secondaryColor,
                        },

                        '&:focus': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColor}, \
                                        0 0 0 3px ${customizations.primaryColorContrast}`,
                        },

                        '&.Mui-selected': {
                            '&:hover': {
                                backgroundColor: darken(customizations.primaryColor, 0.2),
                                color: customizations.primaryColorContrast,
                            },
                            '&:focus': {
                                boxShadow: `0 0 0 2px ${customizations.primaryColorContrast}, \
                                            0 0 0 3px ${customizations.primaryColor}`,
                            },

                            '@media (hover: none)': {
                                '&:focus,&:active,&:hover': {
                                    borderColor: `${customizations.primaryColor} !important`,
                                    backgroundColor: customizations.primaryColor,
                                    color: customizations.primaryColorContrast,
                                },
                            },
                            borderColor: `${customizations.primaryColor} !important`,
                            backgroundColor: customizations.primaryColor,
                            color: customizations.primaryColorContrast,
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        backgroundColor: `${surfaceColor} !important`,
                        color: customizations.inputFieldTextColor,
                        borderTopLeftRadius: customizations.borderRadius,
                        borderTopRightRadius: customizations.borderRadius,
                        borderBottomLeftRadius: customizations.borderRadius,
                        borderBottomRightRadius: customizations.borderRadius,
                        '&:hover,&:focus,&:active,&.Mui-focused': {
                            boxShadow: `0px 0px 2px 2px ${
                                getPrimaryColors(
                                    customizations.primaryColor,
                                    customizations.primaryColorContrast,
                                    surfaceColor,
                                    true,
                                ).backgroundColor
                            }`,
                        },
                        // Browser-autofilled fields: paint over the browser's own autofill
                        // background with the inset-shadow trick so an autofilled field looks
                        // identical to a normal one — surfaceColor/inputFieldTextColor are exactly
                        // the pair a normal field renders with, in any tenant theme. The text
                        // color is forced in BOTH modes: the browser picks its autofill/preview
                        // text color from the page's color-scheme, not the widget theme, so a
                        // dark-scheme host page paints white autofill text into light-themed
                        // widget fields (invisible) unless overridden here.
                        [webkitAutofillSelector]: {
                            WebkitBoxShadow: `0 0 0 100px ${surfaceColor} inset !important`,
                            WebkitTextFillColor: `${customizations.inputFieldTextColor} !important`,
                            caretColor: customizations.inputFieldTextColor,
                            // Mask Chrome's brief autofill repaint flash on focus.
                            transition: 'background-color 9999s ease-in-out 0s',
                        },
                        // Firefox uses the standard :autofill pseudo-class. MUST stay a separate
                        // rule (not comma-joined with -webkit-autofill above): an unrecognized
                        // selector in a comma list drops the entire list in older Safari, which
                        // would silently kill the Chrome/Safari rule too.
                        '& input:autofill': {
                            boxShadow: `0 0 0 100px ${surfaceColor} inset !important`,
                            WebkitTextFillColor: `${customizations.inputFieldTextColor} !important`,
                            caretColor: customizations.inputFieldTextColor,
                        },
                        // Dark mode only: disabled inputs default to
                        // text.disabled (rgba(255,255,255,0.5)) which renders
                        // copied-from-contact-person values nearly illegible
                        // on a dark surface. Lift to alpha(inputFieldTextColor,
                        // 0.7) so the values stay readable while still reading
                        // as visually disabled. Light mode keeps MUI's default
                        // (rgba(0,0,0,0.38) on white has fine contrast).
                        // -webkit-text-fill-color is required: inputs render
                        // disabled text via that property and `color` alone
                        // doesn't take effect on `<input disabled>`.
                        ...(isDark && {
                            '&.Mui-disabled': {
                                color: alpha(customizations.inputFieldTextColor, 0.7),
                                '& .MuiInputBase-input': {
                                    WebkitTextFillColor: alpha(
                                        customizations.inputFieldTextColor,
                                        0.7,
                                    ),
                                },
                            },
                        }),
                    },
                    input: {
                        lineHeight: '1em !important',
                        height: '1.1876em',
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: customizations.inputFieldLabelColor,
                        whiteSpace: 'normal',
                        // See MuiInputBase above — same pattern. Dark mode's
                        // text.disabled (rgba(255,255,255,0.5)) makes labels
                        // for disabled fields fade more than necessary.
                        ...(isDark && {
                            '&.Mui-disabled': {
                                color: alpha(customizations.inputFieldLabelColor, 0.7),
                            },
                        }),
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 2px ${customizations.primaryColorContrast}, \
                                        0 0 0 3px ${customizations.primaryColor}`,
                        },
                    },
                    underline: {
                        '&::before,&:hover::before,&::after,&:hover::after': {
                            borderColor: 'transparent !important',
                            border: 0,
                        },
                    },
                    multiline: {
                        padding: `0 !important`,
                    },
                    input: {
                        padding: '10px !important',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '& fieldset ': {
                            transition: 'border-color 0.255s',
                            borderColor: customizations.inputFieldBorderColor,
                        },
                        color: customizations.inputFieldTextColor,
                        '&:hover,&:focus,&:active,&.Mui-focused': {
                            boxShadow: 'none',
                            borderColor: getPrimaryColors(
                                customizations.primaryColor,
                                customizations.primaryColorContrast,
                                surfaceColor,
                                true,
                            ).backgroundColor,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: getPrimaryColors(
                                customizations.primaryColorContrast,
                                customizations.primaryColor,
                                surfaceColor,
                                true,
                            ).backgroundColor,
                        },
                    },
                    multiline: {
                        padding: `10.5px 14px`,
                    },
                    input: {
                        padding: '10px !important',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        boxSizing: 'border-box',
                        padding: customizations.baseUnit * 2,
                        borderRadius: customizations.borderRadius,
                        // In dark mode, MUI applies a semi-transparent white
                        // "elevation overlay" (background-image linear-gradient)
                        // to Paper based on its elevation prop. That causes
                        // Popover/Dialog/Menu surfaces to render lighter than
                        // widgetBackgroundColor, while child components like
                        // DateCalendar — which read background.paper directly
                        // without the overlay — render darker. Net result: a
                        // visible two-tone surface inside a single popover.
                        // Disabling the overlay gives one consistent dark
                        // surface across all Paper-based components.
                        ...(isDark && { backgroundImage: 'none' }),
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontSize: customizations.navigationTabFontSize ?? customizations.fontSize,
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    popper: {
                        zIndex: `${zIndex.alwaysVisible} !important` as any,
                    },
                    input: {
                        padding: '4px !important',
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    root: {
                        zIndex: `${zIndex.alwaysVisible} !important` as any,
                        '& MuiToolbar-root': {
                            backgroundColor: customizations.bookingWidgetColor,
                        },
                    },
                    paper: {
                        padding: 0,
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        color: `${customizations.secondaryColor} `,
                    },
                },
            },
            MuiSlider: {
                styleOverrides: {
                    root: {
                        height: 4,
                        color: customizations.bookingWidgetColor,
                    },
                    mark: {
                        height: 0,
                    },
                    track: {
                        height: 4,
                    },
                    rail: {
                        height: 4,
                    },
                    thumb: {
                        background: surfaceColor,
                        backgroundColor: surfaceColor,
                        borderWidth: 2,
                        borderStyle: 'solid',
                        borderColor: customizations.bookingWidgetColor,
                        width: 20,
                        height: 20,
                    },
                },
            },
            // Calendar text colors. Light mode falls through to MUI's defaults
            // (text.primary / text.secondary) — byte-identical for existing
            // tenants. Dark mode forces tenant bodyColor on every calendar
            // surface that would otherwise read as low-contrast gray on the
            // dark page (weekday labels, header label, view-switch arrow,
            // month-switch arrows).
            ...(isDark && {
                MuiDateCalendar: {
                    styleOverrides: {
                        root: {
                            color: customizations.bodyColor,
                        },
                    },
                },
                MuiDayCalendar: {
                    styleOverrides: {
                        weekDayLabel: {
                            color: customizations.bodyColor,
                        },
                    },
                },
                MuiPickersCalendarHeader: {
                    styleOverrides: {
                        label: {
                            color: customizations.bodyColor,
                        },
                        switchViewButton: {
                            color: customizations.bodyColor,
                        },
                    },
                },
                MuiPickersArrowSwitcher: {
                    styleOverrides: {
                        button: {
                            color: customizations.bodyColor,
                        },
                    },
                },
            }),
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        borderRadius: customizations.borderRadius > 0 ? '50%' : 0,
                        fontFamily: customizations.bodyFont,
                        fontSize: customizations.bodySize,
                        // Light mode: pin to '#000' (the literal that MUI's
                        // default theme exposes via common.black) to preserve
                        // byte-identical rendering for tenants who customize
                        // bodyColor. Dark mode: route to bodyColor so day
                        // numbers follow the tenant's on-surface text tone.
                        color: isDark ? customizations.bodyColor : '#000',
                        // Disabled-day styling is dark-mode-only. Light mode
                        // falls through to MUI's default disabled rendering
                        // (text.disabled color, MUI's own opacity) — preserves
                        // byte-identical rendering for existing tenants.
                        ...(isDark && {
                            '&.Mui-disabled': {
                                // In dark mode, push the fade into the text
                                // color via alpha rather than putting
                                // opacity: 0.4 on the whole button. Reason:
                                // the date-range picker renders an
                                // unavailable-period bar as an absolute
                                // child of each disabled day. Adjacent bars
                                // overflow into each other (~9px overlap)
                                // to form a continuous strip. With
                                // opacity: 0.4 on every disabled button,
                                // each cell becomes its own 40% stacking
                                // context — overlapping regions blend to
                                // ~64% effective bar color while
                                // single-bar regions stay at 40%, producing
                                // a visible "ribbed" pattern at every cell
                                // boundary on dark surfaces. Targeting the
                                // text color directly leaves the bar at
                                // full opacity and the strip reads as one
                                // clean band.
                                color: `${alpha(customizations.bodyColor, 0.4)} !important`,
                            },
                        }),
                        '&.Mui-selected': {
                            backgroundColor: customizations.bookingWidgetColor,
                            color: customizations.bookingWidgetPrimaryColor,
                        },
                        '&:focus': {
                            backgroundColor: isDark
                                ? alpha(customizations.bookingWidgetColor, 0.4)
                                : lighten(customizations.bookingWidgetColor, 0.7),
                        },

                        '&.Mui-selected:focus': {
                            border: `2px solid ${
                                isDark
                                    ? alpha(customizations.bookingWidgetColor, 0.4)
                                    : lighten(customizations.bookingWidgetColor, 0.7)
                            }`,
                        },
                    },
                },
            },
            MuiDateRangePickerDay: {
                styleOverrides: {
                    root: {
                        '&.MuiDateRangePickerDay-rangeIntervalDayHighlight:first-of-type': {
                            borderTopLeftRadius: customizations.borderRadius > 0 ? '50%' : 0,
                            borderBottomLeftRadius: customizations.borderRadius > 0 ? '50%' : 0,
                        },
                        '&.MuiDateRangePickerDay-rangeIntervalDayHighlight:last-of-type': {
                            borderTopRightRadius: customizations.borderRadius > 0 ? '50%' : 0,
                            borderBottomRightRadius: customizations.borderRadius > 0 ? '50%' : 0,
                        },
                    },
                },
            },
        },
    });

    return createTheme(theme);
};
